import {
  eventSpotCheckToAgentSeed,
  scheduledSpotCheckToAgentSeed,
} from '@solinkcorp/agent/lib/spotCheckToAgentSeed';
import { type CHCameraId, type DeviceEntity } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { Modal, toast } from '@solinkcorp/solink-components';
import { setFilter } from 'actions/filterActions';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import { Sparkles } from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useControllerState } from 'solox';
import useDevices from 'src/hooks/devices/useDevices';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import { useDispatch, useSelector } from 'stores/store';
import isShallowEqual from 'utils/isShallowEqual';
import { getSiteFeatures } from 'utils/LocationUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { EVENTS_URL, VIDEO_URL_PREFIX } from 'utils/urls';
import { AGENTS_URL, DEFAULT_TIMEZONE } from 'vd-constants';
import useAiNotifications, { useCategorizedEventSpotChecks } from './hooks/useAiNotifcations';
import useCustomerSpotCheckUsage from './hooks/useCustomerSpotCheckUsage';
import { useEventSpotCheckPreview } from './hooks/useEventSpotCheckPreview';
import { useEventSpotCheckStructuredOutput } from './hooks/useEventSpotCheckStructuredOutput';
import useIsolatedDeviceSpotCheckUsage from './hooks/useIsolatedDeviceSpotCheckUsage';
import useSpotCheckForm, { TIME_TARGETS } from './hooks/useSpotCheckForm';
import useSpotCheckPreview from './hooks/useSpotCheckPreview';
import { useCategorizedSpotChecks } from './hooks/useSpotChecks';
import useSpotChecksMonthlyConsumption from './hooks/useSpotChecksMonthlyConsumption';
import { useFormattedSpotCheckStructuredOutput } from './hooks/useSpotCheckStructuredOutput';
import SpotCheckFooter from './SpotCheckFooter/SpotCheckFooter';
import SpotCheckHeader from './SpotCheckHeader/SpotCheckHeader';
import SpotCheckMainContent from './SpotCheckMainContent/SpotCheckMainContent';
import { type SpotCheckCategoryKey } from './SpotCheckMainContent/SpotCheckTemplatesView/SpotCheckTemplatesView';
import styles from './SpotCheckPanel.module.sass';

interface SpotCheckPanelProps {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  isNewLook?: boolean;
}

export default function SpotCheckPanel(props: SpotCheckPanelProps) {
  const { isModalOpen, openModal, closeModal, isNewLook = false } = props;
  const [view, setView] = React.useState<'templates' | 'spot-check-form'>('templates');
  const [formMode, setFormMode] = React.useState<'create' | 'edit'>('create');
  const [spotCheck, setSpotCheck] = React.useState<ScheduledActivityEntity | undefined>();
  const [eventSpotCheck, setEventSpotCheck] = React.useState<AiNotification | undefined>();
  const [selectedCategoryKey, setSelectedCategoryKey] = React.useState<string | null>('all');
  const [selectedSpotCheckCategoryKey, setSelectedSpotCheckCategoryKey] =
    React.useState<SpotCheckCategoryKey | null>(null);
  const [excludedCameraIds, setExcludedCameraIds] = React.useState<string[]>([]);
  const [selectedCameraIndex, setSelectedCameraIndex] = React.useState<number>(0);
  const [isExistingSpotCheck, setIsExistingSpotCheck] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLocationEventsPage = location.pathname.includes(EVENTS_URL);

  const locale = useLocale();

  const user = useUserSession();

  const eventSpotChecks = useAiNotifications();
  const categorizedEventSpotChecks = useCategorizedEventSpotChecks();

  const { locations, locationsById } = useFluxStore(
    LocationStore,
    (store) => ({
      locations: store.getPhysicalLocations(),
      locationsById: store.getLocationsById(),
    }),
    isShallowEqual,
  );

  const camerasById = useFluxStore(LocationStore, (store) => store.getAllLocationCamerasDict());

  const dispatch = useDispatch();
  const [spotCheckSearchPrompt, setSpotCheckSearchPrompt] = React.useState('');
  const [eventSpotCheckSearchPrompt, setEventSpotCheckSearchPrompt] = React.useState('');

  const { selectedCameraIds, cameraSelectMode, spotCheckMode } = useSelector(
    (state) => ({
      cameraSelectMode: state.filter.cameraSelectMode,
      selectedCameraIds: state.filter.selectedCameraIds ?? [],
      spotCheckMode: state.filter.spotCheckMode,
    }),
    isShallowEqual,
  );

  const { devicesBySiteId } = useDevices();

  const playingCamerasController = usePlayingCamerasContext();

  const playingVideo = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds.length > 0,
  );

  const enterCameraSelectMode = () => {
    dispatch(
      setFilter({
        cameraSelectMode: 'spot-check',
        selectedCameraIds,
        spotCheckTitle: spotCheckForm.title,
      }),
    );
    setExcludedCameraIds([]);

    if (playingVideo) {
      playingCamerasController.setPlayingCameras([]);
    }

    if (isLocationEventsPage) {
      navigate(VIDEO_URL_PREFIX);
    }
  };

  const spotCheckStructuredOutput = useFormattedSpotCheckStructuredOutput(spotCheckSearchPrompt);
  const eventSpotCheckStructuredOutput = useEventSpotCheckStructuredOutput(
    eventSpotCheckSearchPrompt,
  );

  const structuredOutputSpotCheck = spotCheckStructuredOutput.data;
  const structuredOutputEventSpotCheck = eventSpotCheckStructuredOutput.data;

  const targetSpotCheck = structuredOutputSpotCheck ?? spotCheck;
  const targetEventSpotCheck = React.useMemo(() => {
    return structuredOutputEventSpotCheck
      ? ({
          ...eventSpotCheck,
          aiChatPrompt: structuredOutputEventSpotCheck?.prompt,
          aiChatAnswerFormat: '',
          aiChatTimeTarget: eventSpotCheck?.aiChatTimeTarget,
          aiChatTimestampOffset: eventSpotCheck?.aiChatTimestampOffset,
          metadata: {
            ...eventSpotCheck?.metadata,
            title: structuredOutputEventSpotCheck?.title,
            aiChatAnswerStates: structuredOutputEventSpotCheck?.states,
          },
        } as AiNotification)
      : eventSpotCheck;
  }, [structuredOutputEventSpotCheck, eventSpotCheck]);
  const isSpotCheckCreationView = view === 'spot-check-form';

  const selectedCameraIdsWithoutExceptions: CHCameraId[] = React.useMemo(
    () => selectedCameraIds.filter((cameraId: CHCameraId) => !excludedCameraIds.includes(cameraId)),
    [selectedCameraIds, excludedCameraIds],
  );

  const selectedLocationIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          selectedCameraIdsWithoutExceptions.map(
            (cameraId: string) => camerasById[cameraId]?.location?.id,
          ),
        ),
      ),
    [selectedCameraIdsWithoutExceptions, camerasById],
  );

  const isLivePreview = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';
  const selectedLocations = selectedLocationIds
    .map((locationId) => locationsById?.[locationId])
    .filter((location): location is VideoDiscoverySite => location !== undefined);

  const targetLocations = selectedLocations.length > 0 ? selectedLocations : locations;

  const isolatedSites = getIsolatedSites(targetLocations, devicesBySiteId);

  const isIsolatedDevice = isolatedSites.length > 0;

  const useCustomerQuota = !isIsolatedDevice && !isLivePreview;

  const useDeviceQuota = isIsolatedDevice && !isLivePreview;

  const isolatedDeviceIds = isolatedSites.flatMap(
    (site) => devicesBySiteId[site.id]?.map((device) => device.id) ?? [],
  );

  const { isLoading: isLoadingCustomerUsageStats, usageStats: customerUsageStats } =
    useCustomerSpotCheckUsage(useCustomerQuota);

  const {
    estimatedTotalIsolatedRemainingQuota,
    isLoading: isLoadingIsolatedDeviceQuota,
    correspondingTotalQuota,
  } = useIsolatedDeviceSpotCheckUsage(useDeviceQuota, isolatedDeviceIds);

  let exhausted = false;
  let remaining = 0;
  let total = 0;
  if (estimatedTotalIsolatedRemainingQuota !== undefined) {
    exhausted = estimatedTotalIsolatedRemainingQuota <= 0;
    remaining = estimatedTotalIsolatedRemainingQuota;
    total = correspondingTotalQuota ?? estimatedTotalIsolatedRemainingQuota;
  } else {
    exhausted = customerUsageStats?.exhausted ?? false;
    remaining = customerUsageStats?.usage?.images.remaining ?? 0;
    total = (customerUsageStats?.usage?.images.count || 0) + remaining;
  }

  const timezone = selectedLocations?.[0]?.timezone ?? DEFAULT_TIMEZONE;

  const spotCheckForm = useSpotCheckForm({
    spotCheck: targetSpotCheck,
    eventSpotCheck: targetEventSpotCheck,
    selectedCameraIds: selectedCameraIdsWithoutExceptions,
    timezoneId: timezone,
    selectedLocations,
  });

  const spotCheckPreview = useSpotCheckPreview();

  const {
    eventImagePreviewUrls,
    sentEventSpotCheckRequest,
    eventSpotCheckAnswer,
    totalEventsInLast30Days,
    reportEvent,
    handleEventSpotCheckPreview,
    setEventImagePreviewUrls,
    setEventSpotCheckAnswer,
  } = useEventSpotCheckPreview(spotCheckForm);

  const resetState = (resetCategoryKeys: boolean = true) => {
    if (resetCategoryKeys) {
      setSelectedCategoryKey('all');
      setSelectedSpotCheckCategoryKey(null);
    }
    setView('templates');
    setSpotCheck(undefined);
    setEventSpotCheck(undefined);
    setIsExistingSpotCheck(false);
    setExcludedCameraIds([]);
    setSelectedCameraIndex(0);
    setSpotCheckSearchPrompt('');
    setEventSpotCheckSearchPrompt('');
    setEventImagePreviewUrls([]);
    setEventSpotCheckAnswer('');
    spotCheckPreview.setEventPreviewId(null);
    spotCheckPreview.setEventPollingState(null);
    spotCheckForm.setSelectedEventReport(undefined);
    spotCheckForm.setEventTimestampOffset(0);
    spotCheckForm.setEventTimeTarget(TIME_TARGETS.START);
    dispatch(setFilter({ selectedCameraIds: [], cameraSelectMode: null }));
  };

  const onClose = () => {
    resetState();
    closeModal();
  };

  const handleGoBack = () => {
    resetState(false);
  };

  const handleSchedule = async () => {
    try {
      if (isEditingSpotCheck) {
        await spotCheckForm.editSpotCheck();
      } else {
        await spotCheckForm.createSpotCheck();
      }
      resetState();
    } catch (error) {
      console.error(error);
    }
  };
  const categorizedSpotChecks = useCategorizedSpotChecks();

  const createSpotCheckPreview = React.useCallback(() => {
    setSelectedCameraIndex(0);
    if (excludedCameraIds.length > 0) {
      setExcludedCameraIds([]);
      dispatch(setFilter({ selectedCameraIds: selectedCameraIdsWithoutExceptions }));
    } else {
      spotCheckPreview.createSpotCheckPreview(
        spotCheckForm.newActivity,
        selectedCameraIdsWithoutExceptions,
      );
    }
  }, [
    spotCheckForm.newActivity,
    selectedCameraIdsWithoutExceptions,
    excludedCameraIds,
    spotCheckPreview,
    dispatch,
  ]);

  const activeScheduledSpotChecks = React.useMemo(
    () => categorizedSpotChecks?.['active'] ?? [],
    [categorizedSpotChecks],
  );

  const activeEventSpotChecks = React.useMemo(
    () => categorizedEventSpotChecks?.['active'] ?? [],
    [categorizedEventSpotChecks],
  );

  const {
    restOfMonthConsumption: spotChecksRestOfMonthConsumption,
    loading: loadingSpotCheckConsumption,
  } = useSpotChecksMonthlyConsumption({
    eventSpotChecks: activeEventSpotChecks,
    scheduledSpotChecks: activeScheduledSpotChecks,
  });

  const availableQuota = Math.round(remaining - spotChecksRestOfMonthConsumption);

  const hasExhaustedConsumption =
    exhausted || (typeof availableQuota === 'number' && availableQuota <= 0);

  const displayLoadingQuota =
    loadingSpotCheckConsumption || isLoadingCustomerUsageStats || isLoadingIsolatedDeviceQuota;

  const isEditingSpotCheck = formMode === 'edit';

  const isLoadingPreview = spotCheckPreview.eventPollingState === 'POLLING';

  // Event Spot Check quota estimation
  // The number of events in report over the past 30 days must be less than 1% of monthly customer quota per day (~30%)
  // Updated: restriction being removed https://solink.atlassian.net/browse/SL-6575 @Oct 22, 2025
  const imagesUsage = customerUsageStats?.usage?.images;
  const eventsMonthlyQuotaLimit = imagesUsage?.remaining
    ? imagesUsage?.count + imagesUsage?.remaining
    : 0;

  const isEventsEstimateBelowLimit =
    !!totalEventsInLast30Days && imagesUsage?.remaining && !!spotCheckForm.selectedEventReport
      ? totalEventsInLast30Days <= eventsMonthlyQuotaLimit
      : true;

  const canScheduleEventSpotCheck =
    !!spotCheckForm.selectedEventReport &&
    !sentEventSpotCheckRequest &&
    !hasExhaustedConsumption &&
    isEventsEstimateBelowLimit &&
    spotCheckForm.isValidEventPreview &&
    !loadingSpotCheckConsumption;

  const canScheduleScheduledSpotCheck =
    !hasExhaustedConsumption &&
    !isLivePreview &&
    spotCheckForm.isValidSpotCheck &&
    !loadingSpotCheckConsumption;

  const handleCreateEventSpotCheck = async () => {
    const { cloudId, resellerId, customerId } = user.jwtUser.app_metadata;
    const {
      title,
      selectedEventReport,
      aiChatPrompt,
      aiChatAnswerStates,
      eventTimeTarget,
      eventTimestampOffset,
    } = spotCheckForm;
    const eventSpotCheck: AiNotification = {
      cloudId,
      resellerId,
      customerId: customerId ?? '',
      reportTemplateId: selectedEventReport?.templateId,
      aiChatPrompt,
      aiChatAnswerFormat: '',
      aiChatTimeTarget: eventTimeTarget,
      aiChatTimestampOffset: eventTimestampOffset * 1000, //Convert seconds to milliseconds
      metadata: {
        title,
        aiChatAnswerStates: aiChatAnswerStates.map(({ name, description }) => ({
          name,
          description,
        })),
      },
    };

    try {
      await eventSpotChecks.createAiNotification(eventSpotCheck);
      toast.success(locale.scheduledAIChat.eventSpotCheckScheduledMessageSuccess);
      closeModal();
    } catch (error: any) {
      if (error?.response?.status === 400) {
        toast.error(locale.scheduledAIChat.eventSpotCheckAlreadyInUseReport);
      } else {
        toast.error(locale.scheduledAIChat.eventSpotCheckScheduledMessageFailure);
      }
      throw error;
    }
  };

  const handleUpdateEventSpotCheck = async () => {
    if (!eventSpotCheck?.id) return;

    const {
      title,
      selectedEventReport,
      aiChatPrompt,
      aiChatAnswerFormat,
      aiChatAnswerStates,
      eventTimeTarget,
      eventTimestampOffset,
    } = spotCheckForm;
    const newEventSpotCheck: Partial<AiNotification> = {
      reportTemplateId: selectedEventReport?.templateId,
      aiChatPrompt,
      aiChatAnswerFormat,
      aiChatTimeTarget: eventTimeTarget,
      aiChatTimestampOffset: eventTimestampOffset * 1000, //Convert seconds to milliseconds
      metadata: {
        aiChatAnswerStates: aiChatAnswerStates.map(({ name, description }) => ({
          name,
          description,
        })),
        title,
      },
    };

    try {
      await eventSpotChecks.updateAiNotification(eventSpotCheck.id, newEventSpotCheck);
      toast.success(locale.scheduledAIChat.eventSpotCheckScheduledMessageSuccess);
    } catch (error: any) {
      if (error?.response?.status === 400) {
        toast.error(locale.scheduledAIChat.eventSpotCheckAlreadyInUseReport);
      } else {
        toast.error(locale.scheduledAIChat.eventSpotCheckScheduledMessageFailure);
      }
      throw error;
    }
  };

  const handleRunEventSpotCheck = async () => {
    try {
      if (isEditingSpotCheck) {
        await handleUpdateEventSpotCheck();
      } else {
        await handleCreateEventSpotCheck();
      }
    } catch (error) {
      console.error(error);
      return;
    }
    resetState();
  };

  // Port the spot check the user is editing into a pre-filled `custom-image-search`
  // agent draft, then hand off to the agent creation page. We don't auto-create —
  // the user reviews and submits. See spotCheckToAgentSeed for the field mapping.
  const handleConvertToAgent = () => {
    const agentSeed = isEventSpotCheck
      ? eventSpotCheckToAgentSeed({
          cloudId: '',
          resellerId: '',
          customerId: '',
          reportTemplateId:
            spotCheckForm.selectedEventReport?.templateId ?? eventSpotCheck?.reportTemplateId,
          aiChatPrompt: spotCheckForm.aiChatPrompt,
          aiChatAnswerFormat: '',
          aiChatTimeTarget: spotCheckForm.eventTimeTarget,
          aiChatTimestampOffset: spotCheckForm.eventTimestampOffset * 1000,
          metadata: {
            title: spotCheckForm.title,
            aiChatAnswerStates: spotCheckForm.aiChatAnswerStates.map(({ name, description }) => ({
              name,
              description,
            })),
          },
        })
      : scheduledSpotCheckToAgentSeed(spotCheckForm.newActivity, camerasById);

    onClose();
    navigate(`${AGENTS_URL}/templates/create?template=custom-image-search`, {
      state: { agentSeed },
    });
  };

  const actions = isSpotCheckCreationView
    ? [
        formMode === 'edit' && {
          text: locale.scheduledAIChat.convertToAgent,
          icon: <Sparkles className="mr-2 size-4 shrink-0 align-bottom" aria-hidden />,
          isPrimary: false,
          onClick: handleConvertToAgent,
        },
        {
          text: locale.general.preview,
          isPrimary: false,
          onClick: isEventSpotCheck ? handleEventSpotCheckPreview : createSpotCheckPreview,
          disabled:
            (isEventSpotCheck
              ? !spotCheckForm.isValidEventPreview ||
                sentEventSpotCheckRequest ||
                !totalEventsInLast30Days
              : !spotCheckForm.isValidPreview) || isLoadingPreview,
        },
        {
          text:
            formMode === 'edit'
              ? locale.scheduledAIChat.updateText
              : isEventSpotCheck
                ? locale.scheduledAIChat.eventSpotCheckCreateText
                : locale.scheduledAIChat.schedule,
          onClick: isEventSpotCheck ? handleRunEventSpotCheck : handleSchedule,
          disabled: isEventSpotCheck ? !canScheduleEventSpotCheck : !canScheduleScheduledSpotCheck,
        },
      ].filter((value) => !!value)
    : undefined;

  const isSelectingCameras = cameraSelectMode === 'spot-check';

  return (
    <Modal
      isOpen={isModalOpen && !isSelectingCameras}
      onOpen={openModal}
      onClose={onClose}
      className={styles.spotCheckPanel}
      footerChildren={
        <SpotCheckFooter
          newActivity={spotCheckForm.newActivity}
          view={view}
          totalEventsInLast30Days={totalEventsInLast30Days}
          isEventsEstimateBelowLimit={isEventsEstimateBelowLimit}
          availableQuota={availableQuota}
          displayLoadingQuota={displayLoadingQuota}
          estimatedTotalIsolatedRemainingQuota={estimatedTotalIsolatedRemainingQuota}
          configuredTotalQuota={total}
        />
      }
      type="full-width"
      actions={actions}
      title={
        <SpotCheckHeader
          view={view}
          selectedLocationIds={selectedLocationIds}
          selectedCameraIds={selectedCameraIdsWithoutExceptions}
          enterCameraSelectMode={enterCameraSelectMode}
          resetSpotCheckPanel={resetState}
          isNewLook={isNewLook}
        />
      }
    >
      <SpotCheckMainContent
        view={view}
        setView={setView}
        categorizedSpotChecks={categorizedSpotChecks}
        categorizedEventSpotChecks={categorizedEventSpotChecks}
        setSpotCheck={setSpotCheck}
        setEventSpotCheck={setEventSpotCheck}
        spotCheck={targetSpotCheck}
        eventSpotCheck={targetEventSpotCheck}
        spotCheckForm={spotCheckForm}
        selectedCategoryKey={selectedCategoryKey}
        setSelectedCategoryKey={setSelectedCategoryKey}
        selectedSpotCheckCategoryKey={selectedSpotCheckCategoryKey}
        setSelectedSpotCheckCategoryKey={setSelectedSpotCheckCategoryKey}
        spotCheckSearchPrompt={spotCheckSearchPrompt}
        setSpotCheckSearchPrompt={setSpotCheckSearchPrompt}
        eventSpotCheckSearchPrompt={eventSpotCheckSearchPrompt}
        setEventSpotCheckSearchPrompt={setEventSpotCheckSearchPrompt}
        spotCheckPreview={spotCheckPreview}
        createSpotCheckPreview={createSpotCheckPreview}
        spotCheckStructuredOutput={spotCheckStructuredOutput}
        eventSpotCheckStructuredOutput={eventSpotCheckStructuredOutput}
        setFormMode={setFormMode}
        handleGoBack={handleGoBack}
        excludedCameraIds={excludedCameraIds}
        setExcludedCameraIds={setExcludedCameraIds}
        selectedCameraIndex={selectedCameraIndex}
        setSelectedCameraIndex={setSelectedCameraIndex}
        timezoneId={timezone}
        formMode={formMode}
        setIsExistingSpotCheck={setIsExistingSpotCheck}
        isExistingSpotCheck={isExistingSpotCheck}
        enterCameraSelectMode={enterCameraSelectMode}
        eventImagePreviewUrls={eventImagePreviewUrls}
        sentEventSpotCheckRequest={sentEventSpotCheckRequest}
        eventSpotCheckAnswer={eventSpotCheckAnswer}
        totalEventsInLast30Days={totalEventsInLast30Days}
        isEventsEstimateBelowLimit={isEventsEstimateBelowLimit}
        eventsMonthlyQuotaLimit={eventsMonthlyQuotaLimit}
        reportEvent={reportEvent}
      />
    </Modal>
  );
}

function getIsolatedSites(
  sites: VideoDiscoverySite[],
  devicesBySiteId: Record<string, DeviceEntity[]>,
) {
  return sites.filter((site) => !!getSiteFeatures(devicesBySiteId[site.id])?.usage?.isolated);
}
